// Lead Pipeline — mirrors Deal Pipeline for net-new customers (no prior orders).
// Shared under both `Sales & Orders › Lead Pipeline` and `CRM › Lead Pipeline`.
// A person is a Lead when NumberOfOrders == 0 && LTV == 0 (or IsLead=true).
// When their first order lands, they auto-convert into a Deal (placeholder in
// order webhook — see backend/server.py).
import ActivityCell from "../../components/activity/ActivityCell";
import useActivitySummaries from "../../components/activity/useActivitySummaries";
import { useEffect, useMemo, useState, useCallback, useRef, createContext, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Sprout, Search, LayoutGrid, Table as TableIcon,
  ChevronDown, ChevronRight, ChevronLeft, ArrowUpDown, ArrowUp, ArrowDown, Loader2, RefreshCw,
  AlertTriangle, FileText, ExternalLink, MapPin, X, Plus,
  UserPlus, Mail, Phone as PhoneIcon, Archive, CheckSquare, Square, Layers, Eye, UserCheck,
} from "lucide-react";
import QuotePeekModal from "../../components/QuotePeekModal";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import BufferProgressBar from "../../components/BufferProgressBar";
import { useTheme } from "../../context/ThemeContext";
import SavedViewChips, { useSavedViews } from "../../components/SavedViewChips";
import PipelineStageFilter from "../../components/PipelineStageFilter";
import FilteredStatsBox from "../../components/FilteredStatsBox";
import { normalizeDateFilter, isDateFilterActive, dateFilterLabel, applyDateFilterParams, EMPTY_DATE_FILTER } from "../../lib/dateRange";
import DateRangeFilter from "../../components/DateRangeFilter";

const LEAD_DATE_FIELDS = [{ value: "", label: "Last activity" }];
import Person360Overlay from "../../components/Person360Overlay";
import { NotesPeekProvider, NotesButton } from "../../components/notes/NotesPeek";
import InfiniteSentinel from "../../components/InfiniteSentinel";
import LiveCount from "../../components/LiveCount";
import { OwnerChip } from "../../components/OwnerChip";
import { OwnerAssignChip } from "../../components/OwnerAssignChip";
import { useAccountManagers } from "../../hooks/useAccountManagers";
import { useAuth } from "../../context/AuthContext";

const TIER_OPTS = ["BLACK", "PLATINUM", "GOLD", "SILVER", "BRONZE"];

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Carries the "open central quote-peek" callback so nested rows/cards can
// trigger it without prop-drilling (mirrors the Deal pipeline).
const LeadQuotePeekCtx = createContext(null);

const relTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  const days = Math.round((Date.now() - +d) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1d";
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
};

const STAGE_COLOR = {
  slate:   "text-slate-300 border-slate-500/40 bg-slate-500/[0.06]",
  sky:     "text-sky-300 border-sky-500/40 bg-sky-500/[0.06]",
  purple:  "text-purple-300 border-purple-500/40 bg-purple-500/[0.06]",
  orange:  "text-orange-300 border-orange-500/40 bg-orange-500/[0.06]",
  amber:   "text-amber-300 border-amber-500/40 bg-amber-500/[0.06]",
  emerald: "text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.06]",
  rose:    "text-rose-300 border-rose-500/40 bg-rose-500/[0.06]",
  cyan:    "text-cyan-300 border-cyan-500/40 bg-cyan-500/[0.06]",
};
const stageChipCls = (c) => STAGE_COLOR[c] || STAGE_COLOR.slate;

function SortTh({ label, sortKey, activeKey, dir, onSort, align = "left" }) {
  const active = activeKey === sortKey;
  return (
    <th className={`py-2 pr-3 select-none ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`leads-th-${sortKey}`}
        className={`inline-flex items-center gap-1 font-mono uppercase tracking-widest hover:text-white transition-colors ${active ? "text-sky-100" : "text-sky-200/80"} ${align === "right" ? "flex-row-reverse" : ""}`}
        title={`Sort by ${label}`}
      >
        {label}
        {active
          ? (dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </button>
    </th>
  );
}

function ChipRow({ label, options, selected, onChange, facets, testid, allowMulti = true }) {
  const has = (v) => selected.includes(v);
  const toggle = (v) => {
    if (!allowMulti) { onChange(has(v) ? [] : [v]); return; }
    onChange(has(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 mr-1">{label}</span>
      {options.map((o) => {
        const val = typeof o === "string" ? o : o.value;
        const lbl = typeof o === "string" ? o : o.label;
        const count = facets?.[val] ?? null;
        const active = has(val);
        return (
          <button
            key={val}
            type="button"
            onClick={() => toggle(val)}
            data-testid={`chip-${testid}-${val}`}
            className={`px-2 py-0.5 rounded-md text-[10.5px] font-mono uppercase tracking-widest border flex items-center gap-1 transition-colors ${
              active
                ? "bg-sky-500/15 border-sky-500/50 text-sky-200"
                : "border-white/10 text-slate-400 hover:text-white hover:border-white/25"
            }`}
          >
            {lbl}
            {count != null && <span className="text-[9px] text-slate-500 tabular-nums">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

function LeadRow({ lead, stages, onNavigate, expanded, onToggleExpand, onNewQuote, onQuoteArchived, onStageChange, onOwnerChanged, selected, onToggleSelect, activitySummary, onActivitySaved }) {
  const navigate = useNavigate();
  const openPeek = useContext(LeadQuotePeekCtx);
  const stage = stages.find((s) => s.key === lead.stage_key);
  const stageColor = stage?.color;
  const stageLabel = stage?.label || "New Lead";

  return (
    <>
      <tr
        data-testid={`lead-row-${lead.id}`}
        onClick={() => onNavigate(lead.id)}
        className={`border-b border-white/[0.04] hover:bg-white/[0.03] cursor-pointer ${selected ? "bg-teal-500/[0.06]" : ""}`}
      >
        <td className="py-2.5 pl-3 pr-1 w-6" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => onToggleSelect(lead.id)}
            className="w-5 h-5 grid place-items-center rounded text-slate-500 hover:text-teal-300"
            data-testid={`lead-select-${lead.id}`}
            aria-label={selected ? "Deselect" : "Select"}
          >
            {selected ? <CheckSquare className="w-4 h-4 text-teal-300" /> : <Square className="w-4 h-4" />}
          </button>
        </td>
        <td className="py-2.5 pl-1 pr-2 w-8">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleExpand(lead.id); }}
            className="w-5 h-5 grid place-items-center rounded text-slate-500 hover:text-white hover:bg-white/5"
            data-testid={`lead-expand-${lead.id}`}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        </td>
        <td className="py-2.5 pr-3">
          <div className="flex items-center gap-2">
            <div className="text-[13px] font-medium text-white truncate max-w-[240px]">
              {lead.full_name || "—"}
            </div>
            <NotesButton personId={lead.id} name={lead.full_name || lead.company} compact />
          </div>
          <div className="text-[11px] text-slate-400 truncate max-w-[280px]">
            {lead.company && <span>{lead.company}</span>}
            {lead.company && lead.email && <span className="text-slate-500"> · </span>}
            {lead.email && <span className="text-slate-500">{lead.email}</span>}
          </div>
        </td>
        <td className="py-2.5 pr-3">
          <select
            value={lead.stage_key || (stages[0]?.key || "new_lead")}
            onChange={(e) => { e.stopPropagation(); onStageChange(lead.id, e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            className={`dark-select inline-block px-1.5 py-1 rounded border font-mono uppercase tracking-wider text-[9.5px] hover:bg-white/[0.06] focus:outline-none focus:ring-1 focus:ring-sky-500/50 cursor-pointer ${stageChipCls(stageColor)}`}
            data-testid={`lead-stage-select-${lead.id}`}
            title="Change lead stage — auto-syncs across the customer 360"
          >
            {stages.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          {lead.is_rotting && (
            <div className="mt-1 text-[9px] font-mono uppercase tracking-widest text-rose-300 flex items-center gap-1">
              <AlertTriangle className="w-2.5 h-2.5" /> +{lead.rot_days_over}d rot
            </div>
          )}
        </td>
        <td className="py-2.5 pr-3 text-[11.5px] text-slate-400 truncate max-w-[140px]">
          {lead.source || <span className="text-slate-600">—</span>}
        </td>
        <td className="py-2.5 pr-3 text-[11.5px] text-slate-400 whitespace-nowrap">
          {lead.phone || <span className="text-slate-600">—</span>}
        </td>
        <td className="py-2.5 pr-3 tabular-nums text-right text-[12px]">
          <div className="flex items-center gap-1 justify-end">
            <span className={`inline-flex items-center gap-1 ${lead.quote_count ? "text-orange-300" : "text-slate-600"}`}>
              <FileText className="w-3 h-3" />{lead.quote_count}
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onNewQuote(lead.id); }}
              data-testid={`lead-new-quote-${lead.id}`}
              className="ml-1 w-5 h-5 grid place-items-center rounded text-slate-500 hover:text-orange-300 hover:bg-orange-500/10"
              aria-label={`Create quote pre-attached to ${lead.full_name}`}
              title={`New quote for ${lead.full_name}`}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
        <td className="py-2.5 pr-3 max-w-[150px]" onClick={(e) => e.stopPropagation()}>
          <OwnerAssignChip personId={lead.id} primary={lead.am} onChanged={onOwnerChanged} />
        </td>
        <td className="py-2.5 pr-3 text-[10.5px] text-slate-500 font-mono">
          {lead.post_code && (
            <span className="inline-flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{lead.post_code}</span>
          )}
        </td>
        <td className="py-2.5 pr-3" onClick={(e) => e.stopPropagation()}>
          <ActivityCell personId={lead.id} am={lead.am} summary={activitySummary} onSaved={onActivitySaved} />
        </td>
        <td className="py-2.5 pr-3 text-[10.5px] text-slate-500 font-mono whitespace-nowrap" title="Last updated">
          {relTime(lead.last_updated)}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-white/[0.02]" data-testid={`lead-row-${lead.id}-expanded`}>
          <td></td>
          <td colSpan={10} className="py-3 pr-3">
            {lead.quotes.length === 0 ? (
              <div className="flex items-center gap-3 text-[11px] text-slate-500 font-mono px-3 py-4 border border-dashed border-white/[0.06] rounded">
                No quotes for this lead yet —{" "}
                <Link
                  to={`/quotes/new?customer=${lead.id}`}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`lead-new-quote-empty-${lead.id}`}
                  className="text-orange-300 hover:underline inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> create the first one
                </Link>
              </div>
            ) : (
              <div className="space-y-1.5 pl-3">
                <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 mb-1">
                  {lead.quotes.length} quote{lead.quotes.length === 1 ? "" : "s"}
                </div>
                {lead.quotes.map((q) => (
                  <div
                    key={q.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-md border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); navigate(`/quotes/${q.id}/edit`); }}
                    data-testid={`lead-quote-${q.quote_ref || q.id}`}
                  >
                    <FileText className="w-3.5 h-3.5 text-orange-300" />
                    <span className="font-mono text-[11.5px] text-orange-200">{q.quote_ref || `#${q.id.slice(-6)}`}</span>
                    <span className="text-[11px] text-slate-400 uppercase tracking-widest font-mono">{q.status}</span>
                    <span className="ml-auto text-[10px] text-slate-500 font-mono">{relTime(q.updated)}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openPeek?.(q.id); }}
                      className="text-slate-500 hover:text-teal-300 p-1 -m-1"
                      title={`Peek at ${q.quote_ref}`}
                      data-testid={`lead-quote-peek-${q.quote_ref || q.id}`}
                    >
                      <Eye className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Archive quote ${q.quote_ref}?`)) return;
                        try {
                          await axios.post(`${API}/quotes/${q.id}/archive`, { reason: "Archived from Lead Pipeline" }, { withCredentials: true });
                          onQuoteArchived?.(q.id);
                        } catch { /* silent */ }
                      }}
                      className="text-slate-500 hover:text-amber-300 p-1 -m-1"
                      title={`Archive ${q.quote_ref}`}
                      data-testid={`lead-quote-archive-${q.quote_ref || q.id}`}
                    >
                      <Archive className="w-3 h-3" />
                    </button>
                    <ExternalLink className="w-3 h-3 text-slate-500" />
                  </div>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

const LEAD_QSTATUS_TONE = {
  DRAFT: "text-slate-400 bg-white/5 border-white/10",
  SENT: "text-sky-300 bg-sky-500/10 border-sky-500/30",
  WON: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  ACCEPTED: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  LOST: "text-rose-300 bg-rose-500/10 border-rose-500/30",
  EXPIRED: "text-amber-300 bg-amber-500/10 border-amber-500/30",
};
const leadGbp = (n) => (n == null || n === "" ? "" : "£" + Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 }));
function LeadQStatusPill({ status }) {
  const tone = LEAD_QSTATUS_TONE[String(status || "").toUpperCase()] || "text-slate-400 bg-white/5 border-white/10";
  return <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border shrink-0 ${tone}`}>{status || "—"}</span>;
}

function BoardCard({ card, onNavigate }) {
  const [expanded, setExpanded] = useState(false);
  const openPeek = useContext(LeadQuotePeekCtx);
  const stop = (e) => e.stopPropagation();
  const quotes = Array.isArray(card.quotes) ? card.quotes : [];
  const primary = quotes.find((q) => q.id === card.master_quote_id) || quotes[0] || null;
  const rest = quotes.filter((q) => q !== primary);

  return (
    <div
      onClick={() => onNavigate(card.id)}
      data-testid={`lead-board-card-${card.id}`}
      className="group rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-teal-500/30 p-2.5 cursor-pointer transition-all"
    >
      {/* 1 · Identity */}
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-white leading-tight truncate">{card.full_name || "Unnamed"}</div>
        {card.company && <div className="text-[11px] text-slate-400 leading-tight truncate">{card.company}</div>}
      </div>

      {/* 2 · Owner + contact */}
      <div className="flex items-center justify-between gap-2 mt-1.5 pt-1.5 border-t border-white/[0.06]">
        {card.am ? <OwnerChip primary={card.am} /> : <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Unassigned</span>}
        {card.email && (
          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-mono text-slate-500 truncate max-w-[130px]" title={card.email}>
            <Mail className="w-2.5 h-2.5 shrink-0" />{card.email}
          </span>
        )}
      </div>

      {/* 3 · Permanent primary quote island */}
      <div className="mt-2 rounded-md border border-white/[0.08] bg-black/20 p-2" onClick={stop}>
        {primary ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono font-bold text-[11px] text-orange-300 tracking-wide truncate">{primary.quote_ref}</span>
              <LeadQStatusPill status={primary.status} />
            </div>
            <div className="flex items-center justify-between gap-2 mt-1.5">
              <span className="font-mono font-bold tabular-nums text-[12px] text-white">{leadGbp(primary.total) || "—"}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openPeek?.(primary.id); }}
                className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-teal-500/30 bg-teal-500/10 text-teal-200 hover:bg-teal-500/20 text-[10px] font-mono uppercase tracking-wider transition-colors"
                title={`Peek at ${primary.quote_ref}`}
                data-testid={`lead-board-card-quote-peek-${primary.id}`}
              >
                <Eye className="w-3 h-3" /> Peek
              </button>
            </div>
            {rest.length > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
                className="mt-1.5 w-full text-[9.5px] font-mono uppercase tracking-widest text-slate-500 hover:text-teal-300 flex items-center justify-center gap-1"
                data-testid={`lead-board-card-expand-${card.id}`}
              >
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                {expanded ? "Hide" : `+${rest.length} more quote${rest.length > 1 ? "s" : ""}`}
              </button>
            )}
            {expanded && rest.length > 0 && (
              <div className="mt-1.5 pt-1.5 border-t border-white/[0.06] space-y-1">
                {rest.slice(0, 6).map((q) => (
                  <div key={q.id} className="flex items-center gap-2 text-[10.5px]" data-testid={`lead-board-card-secondary-quote-${q.id}`}>
                    <span className="font-mono text-orange-200 truncate">{q.quote_ref}</span>
                    <LeadQStatusPill status={q.status} />
                    <span className="ml-auto tabular-nums font-mono text-slate-200">{leadGbp(q.total)}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openPeek?.(q.id); }}
                      className="shrink-0 p-0.5 rounded hover:bg-teal-500/20 text-teal-300/70 hover:text-teal-200"
                      title={`Peek at ${q.quote_ref}`}
                      data-testid={`lead-board-card-secondary-peek-${q.id}`}
                    >
                      <Eye className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-[10.5px] text-slate-500 italic text-center py-0.5">No quote yet</div>
        )}
      </div>

      {/* 4 · Footer */}
      {(card.last_updated || card.is_rotting) && (
        <div className="flex items-center justify-between gap-2 mt-2 pt-1.5 border-t border-white/[0.06] text-[10px] font-mono">
          <span className="inline-flex items-center gap-2 text-slate-500">{card.last_updated ? relTime(card.last_updated) : ""}<NotesButton personId={card.id} name={card.full_name || card.company} /></span>
          {card.is_rotting && (
            <span className="inline-flex items-center gap-1 text-rose-300 uppercase tracking-widest text-[9.5px]">
              <AlertTriangle className="w-2.5 h-2.5" />+{card.rot_days_over}d
            </span>
          )}
        </div>
      )}
    </div>
  );
}
function BoardView({ leads, stages, onNavigate, onStageChange, containerRef }) {
  const grouped = useMemo(() => {
    const map = Object.fromEntries(stages.map((s) => [s.key, []]));
    for (const l of leads) {
      const k = l.stage_key && map[l.stage_key] ? l.stage_key : (stages[0]?.key || "new_lead");
      map[k].push(l);
    }
    return map;
  }, [leads, stages]);

  const [dragOver, setDragOver] = useState(null);
  const onDragStart = (e, lead) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ id: lead.id, from: lead.stage_key }));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = (e, toKey) => {
    e.preventDefault();
    setDragOver(null);
    try {
      const { id, from } = JSON.parse(e.dataTransfer.getData("text/plain") || "{}");
      if (!id || !toKey || from === toKey) return;
      onStageChange?.(id, toKey);
    } catch { /* silent */ }
  };
  const onDragOver = (e, key) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(key); };

  return (
    <div ref={containerRef} className="-mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 overflow-x-auto pb-4">
      <div className="flex gap-3 min-w-max" data-testid="lead-board">
        {stages.map((s) => {
          const items = grouped[s.key] || [];
          const isDragOver = dragOver === s.key;
          return (
            <div
              key={s.key}
              className={`shrink-0 w-72 rounded-lg border transition-colors ${stageChipCls(s.color).split(" ").slice(1).join(" ")} ${isDragOver ? "ring-2 ring-sky-400/60 bg-sky-500/[0.04]" : ""}`}
              data-testid={`lead-board-col-${s.key}`}
              onDragOver={(e) => onDragOver(e, s.key)}
              onDragLeave={() => setDragOver((cur) => (cur === s.key ? null : cur))}
              onDrop={(e) => onDrop(e, s.key)}
            >
              <div className="px-3 py-2.5 border-b border-white/5">
                <div className={`text-[12.5px] font-semibold truncate ${stageChipCls(s.color).split(" ")[0]}`}>{s.label}</div>
                <div className="mt-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                  <span className="tabular-nums">{items.length} lead{items.length === 1 ? "" : "s"}</span>
                  {s.probability > 0 && <><span>·</span><span>{s.probability}%</span></>}
                </div>
              </div>
              <div className="p-2 space-y-2 min-h-[120px] max-h-[calc(100vh-260px)] overflow-y-auto">
                {items.length === 0 && (
                  <div className="text-center text-[11px] text-slate-600 font-mono py-6 border border-dashed border-white/[0.06] rounded">
                    {isDragOver ? "Drop to move" : "empty"}
                  </div>
                )}
                {items.map((c) => (
                  <div
                    key={c.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, c)}
                    data-testid={`lead-board-card-drag-${c.id}`}
                  >
                    <BoardCard card={c} onNavigate={onNavigate} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── New Lead modal ─── */
function NewLeadModal({ open, onClose, onCreated }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", post_code: "" });
  const [dups, setDups] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setF({ first_name: "", last_name: "", email: "", phone: "", company: "", post_code: "" }); setDups([]); }
  }, [open]);

  const dupCheck = useCallback(async () => {
    const q = new URLSearchParams();
    if (f.email) q.set("email", f.email);
    if (f.phone) q.set("phone", f.phone);
    const nm = [f.first_name, f.last_name].filter(Boolean).join(" ").trim();
    if (nm) q.set("name", nm);
    if (!q.toString()) { setDups([]); return; }
    try {
      const r = await axios.get(`${API}/leads/dup-check?${q.toString()}`, { withCredentials: true });
      setDups(r.data.matches || []);
    } catch (_) { setDups([]); }
  }, [f]);

  useEffect(() => {
    const t = setTimeout(dupCheck, 350);
    return () => clearTimeout(t);
  }, [f.email, f.phone, f.first_name, f.last_name, dupCheck]);

  if (!open) return null;
  const valid = f.first_name.trim() && f.email.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/leads`, f, { withCredentials: true });
      onCreated?.(r.data.person_id);
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create lead");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="new-lead-modal">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg rounded-xl border border-white/10 bg-slate-950 shadow-2xl p-5 space-y-4"
      >
        <div className="flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-sky-300" />
          <div className="text-[10px] uppercase tracking-widest font-mono text-sky-300">New Lead</div>
          <button type="button" onClick={onClose} className="ml-auto w-6 h-6 grid place-items-center rounded hover:bg-white/5 text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="text-xl font-display font-semibold">Capture a net-new customer</h3>

        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-1 text-[11px] text-slate-400">
            First name*
            <input required autoFocus value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })}
              data-testid="new-lead-first-name"
              className="mt-1 w-full px-2 py-2 rounded-md bg-white/[0.04] border border-white/10 text-white outline-none focus:border-sky-500/50" />
          </label>
          <label className="col-span-1 text-[11px] text-slate-400">
            Last name
            <input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })}
              data-testid="new-lead-last-name"
              className="mt-1 w-full px-2 py-2 rounded-md bg-white/[0.04] border border-white/10 text-white outline-none focus:border-sky-500/50" />
          </label>
          <label className="col-span-2 text-[11px] text-slate-400">
            Email*
            <div className="relative">
              <Mail className="w-3.5 h-3.5 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2" />
              <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                data-testid="new-lead-email"
                className="mt-1 w-full pl-7 pr-2 py-2 rounded-md bg-white/[0.04] border border-white/10 text-white outline-none focus:border-sky-500/50" />
            </div>
          </label>
          <label className="col-span-1 text-[11px] text-slate-400">
            Mobile
            <div className="relative">
              <PhoneIcon className="w-3.5 h-3.5 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2" />
              <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                data-testid="new-lead-phone"
                className="mt-1 w-full pl-7 pr-2 py-2 rounded-md bg-white/[0.04] border border-white/10 text-white outline-none focus:border-sky-500/50" />
            </div>
          </label>
          <label className="col-span-1 text-[11px] text-slate-400">
            Postcode
            <input value={f.post_code} onChange={(e) => setF({ ...f, post_code: e.target.value })}
              data-testid="new-lead-postcode"
              className="mt-1 w-full px-2 py-2 rounded-md bg-white/[0.04] border border-white/10 text-white outline-none focus:border-sky-500/50" />
          </label>
          <label className="col-span-2 text-[11px] text-slate-400">
            Company (optional)
            <input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })}
              data-testid="new-lead-company"
              className="mt-1 w-full px-2 py-2 rounded-md bg-white/[0.04] border border-white/10 text-white outline-none focus:border-sky-500/50" />
          </label>
        </div>

        {/* Duplicate matches */}
        {dups.length > 0 && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] p-2.5 space-y-2" data-testid="new-lead-dups">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-amber-300">
              <AlertTriangle className="w-3 h-3" /> Possible duplicates ({dups.length})
            </div>
            <div className="space-y-1">
              {dups.slice(0, 4).map((d) => (
                <Link
                  key={d.id}
                  to={d.is_lead ? `/leads` : `/crm/persons/${d.id}`}
                  onClick={onClose}
                  data-testid={`new-lead-dup-${d.id}`}
                  className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white/5 text-[11.5px]"
                >
                  <span className="text-white font-medium truncate flex-1">{d.full_name}</span>
                  <span className="text-slate-500 truncate max-w-[180px]">{d.email}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-widest ${d.is_lead ? "bg-sky-500/15 text-sky-300 border border-sky-500/40" : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"}`}>
                    {d.is_lead ? "Lead" : "Deal"}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.06]">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md border border-white/10 text-slate-300 hover:text-white hover:bg-white/[0.06] text-[12px]">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || busy}
            data-testid="new-lead-submit"
            className="px-3 py-1.5 rounded-md bg-sky-500 text-white text-[12px] font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-sky-600"
          >
            {busy ? "Creating…" : "Create lead"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function LeadPipeline() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const managers = useAccountManagers();
  const [view, setView] = useState(() => localStorage.getItem("leads.view") || "board");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], stages: [], facets: {}, total: 0 });
  const [search, setSearch] = useState("");
  const [stages, setStages] = useState([]);
  const [sources, setSources] = useState([]);
  const [ams, setAms] = useState([]);
  const [rot, setRot] = useState([]);
  const [hasQuote, setHasQuote] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [dateRange, setDateRange] = useState(EMPTY_DATE_FILTER);
  const [sort, setSort] = useState("created");
  const [expandedIds, setExpandedIds] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [person360Id, setPerson360Id] = useState(null);
  const [peekQuoteId, setPeekQuoteId] = useState(null);

  // Reopen the 360 overlay when returning from a full-profile page (return contract §5).
  const location = useLocation();
  useEffect(() => {
    if (location.state?.open360) setPerson360Id(location.state.open360);
  }, [location.state]);
  const boardRef = useRef(null);
  const [boardHover, setBoardHover] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const updateEdges = useCallback(() => {
    const el = boardRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  }, []);
  const scrollBoard = useCallback((dir) => {
    const el = boardRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8), behavior: "smooth" });
    setTimeout(updateEdges, 450);
  }, [updateEdges]);

  const toggleExpand = (id) => setExpandedIds((m) => ({ ...m, [id]: !m[id] }));
  const newQuoteFor = (personId) => navigate(`/quotes/new?customer=${personId}`);

  const { user } = useAuth();
  // The logged-in user's Account-Manager name (if they are one). Drives "My leads".
  const myAmName = useMemo(() => {
    if (!user || !managers.length) return null;
    const me = managers.find(
      (m) => m.id === user.id
        || (user.email && m.email && m.email.toLowerCase() === user.email.toLowerCase())
        || m.name === user.name
    );
    return me ? me.name : null;
  }, [user, managers]);

  const changeStage = useCallback(async (personId, stageKey) => {
    try {
      await axios.patch(`${API}/leads/${personId}/stage`, { stage_id: stageKey }, { withCredentials: true });
      toast.success("Lead stage updated");
      load();
    } catch (err) {
      toast.error("Failed to change stage", { description: err?.response?.data?.detail || "" });
    }
     
  }, []);

  // ── Client-side header sort of the loaded table page (mirrors Deal pipeline) ──
  const [sortKey, setSortKey] = useState(null);   // null = server/default order
  const [sortDir, setSortDir] = useState("asc");
  const onHeaderSort = useCallback((key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(null); setSortDir("asc");
  }, [sortKey, sortDir]);

  // ── Table multi-select + bulk stage-assign (mirrors Deal pipeline) ──
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const bulkAssignStage = useCallback(async (stageKey) => {
    if (!stageKey || selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = [...selectedIds];
    try {
      const results = await Promise.allSettled(
        ids.map((id) => axios.patch(`${API}/leads/${id}/stage`, { stage_id: stageKey }, { withCredentials: true }))
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok) toast.success(`Moved ${ok} lead${ok === 1 ? "" : "s"}`);
      if (failed) toast.error(`${failed} failed to move`);
      clearSelection();
      load();
    } finally { setBulkBusy(false); }
     
  }, [selectedIds, clearSelection]);

  useEffect(() => { localStorage.setItem("leads.view", view); }, [view]);

  // Per-page cap — bandwidth-conscious for low-bandwidth / offshore users.
  const [pageSize, setPageSize] = useState(() => {
    const saved = Number(localStorage.getItem("leads.pageSize") || 100);
    return [25, 50, 100, 200, 500].includes(saved) ? saved : 100;
  });
  useEffect(() => { localStorage.setItem("leads.pageSize", String(pageSize)); }, [pageSize]);
  // "My leads" mode — defaults ON so an AM lands on their own book (and loads
  // ALL of it, not just one page). Explicit + visible, unlike the old silent filter.
  const [mine, setMine] = useState(() => localStorage.getItem("leads.mine") !== "0");
  useEffect(() => { localStorage.setItem("leads.mine", mine ? "1" : "0"); }, [mine]);
  // Infinite-scroll accumulation for the TABLE view (kanban always shows page 0).
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const genRef = useRef(0);

  // Saved-view presets (one-click filter recall).
  const viewSnapshot = useMemo(() => ({
    search: search.trim(),
    stages, sources, ams, rot, hasQuote, tiers, dateRange,
    sort, view,
  }), [search, stages, sources, ams, rot, hasQuote, tiers, dateRange, sort, view]);
  const savedViews = useSavedViews("leads.savedViews", viewSnapshot);
  const applySavedView = useCallback((snap) => {
    if (!snap) return;
    setSearch(snap.search || "");
    setStages(Array.isArray(snap.stages) ? snap.stages : []);
    setSources(Array.isArray(snap.sources) ? snap.sources : []);
    setAms(Array.isArray(snap.ams) ? snap.ams : []);
    setRot(Array.isArray(snap.rot) ? snap.rot : []);
    setHasQuote(Array.isArray(snap.hasQuote) ? snap.hasQuote : []);
    setTiers(Array.isArray(snap.tiers) ? snap.tiers : []);
    setDateRange(normalizeDateFilter(snap.dateRange));
    if (snap.sort) setSort(snap.sort);
    if (snap.view === "table" || snap.view === "board") setView(snap.view);
  }, []);

  const buildParams = useCallback((offset) => {
    const effMine = mine && myAmName;
    const p = new URLSearchParams({ limit: view === "board" ? 500 : pageSize, offset, sort });
    if (search.trim()) p.set("search", search.trim());
    if (stages.length) p.set("stage", stages.join(","));
    if (sources.length) p.set("source", sources.join(","));
    if (effMine) p.set("am", myAmName);
    else if (ams.length) p.set("am", ams.join(","));
    if (tiers.length) p.set("tier", tiers.join(","));
    if (rot.length === 1) p.set("rot", rot[0]);
    if (hasQuote.length === 1) p.set("has_quote", hasQuote[0]);
    applyDateFilterParams(p, dateRange);
    return p;
  }, [search, stages, sources, ams, tiers, rot, hasQuote, dateRange, sort, pageSize, mine, myAmName, view]);

  const load = useCallback(async () => {
    genRef.current += 1;
    const gen = genRef.current;
    offsetRef.current = 0;
    loadingMoreRef.current = false;
    setLoadingMore(false);
    setLoading(true); setError(null); setHasMore(true);
    try {
      const r = await axios.get(`${API}/leads?${buildParams(0).toString()}`, { withCredentials: true });
      if (gen !== genRef.current) return;
      const items = r.data.items || [];
      setData(r.data);
      offsetRef.current = items.length;
      setHasMore(items.length < (r.data.total_matching ?? r.data.total ?? items.length));
    } catch (e) {
      if (gen === genRef.current) setError(e.response?.data?.detail || "Failed to load leads");
    } finally {
      if (gen === genRef.current) setLoading(false);
    }
  }, [buildParams]);

  // Table-view only: append the next batch as the sentinel scrolls into view.
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore || loading) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const gen = genRef.current;
    try {
      const r = await axios.get(`${API}/leads?${buildParams(offsetRef.current).toString()}`, { withCredentials: true });
      if (gen !== genRef.current) return;
      const batch = r.data.items || [];
      setData((prev) => {
        const seen = new Set((prev.items || []).map((it) => it.id));
        const merged = [...(prev.items || []), ...batch.filter((it) => !seen.has(it.id))];
        return { ...prev, ...r.data, items: merged };
      });
      offsetRef.current += batch.length;
      setHasMore(offsetRef.current < (r.data.total_matching ?? r.data.total ?? offsetRef.current));
    } finally {
      if (gen === genRef.current) { loadingMoreRef.current = false; setLoadingMore(false); }
    }
  }, [buildParams, hasMore, loading]);

  // Background buffered pagination — stream all remaining pages automatically
  // (pageSize batches) until the full set is loaded; fills board view too.
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    if ((data.items?.length || 0) >= 5000) return;
    const t = setTimeout(() => loadMore(), 100);
    return () => clearTimeout(t);
  }, [loading, loadingMore, hasMore, data.items?.length, loadMore]);

  useEffect(() => { load(); }, [load]);

  const activeFilterCount = stages.length + sources.length + ams.length + rot.length + hasQuote.length + tiers.length + (search ? 1 : 0) + (isDateFilterActive(dateRange) ? 1 : 0);
  const clearAll = () => { setSearch(""); setStages([]); setSources([]); setAms([]); setRot([]); setHasQuote([]); setTiers([]); setDateRange(EMPTY_DATE_FILTER); };

  // Reorder the loaded page client-side when a column header is active.
  const sortedItems = useMemo(() => {
    const items = data.items || [];
    if (!sortKey) return items;
    const stageOrder = Object.fromEntries((data.stages || []).map((s, i) => [s.key, i]));
    const val = (l) => {
      switch (sortKey) {
        case "name":     return (l.full_name || l.company || "").toLowerCase();
        case "stage":    return stageOrder[l.stage_key] ?? 999;
        case "source":   return (l.source || "").toLowerCase();
        case "phone":    return (l.phone || "").toLowerCase();
        case "quotes":   return Number(l.quote_count || 0);
        case "am":       return (l.am || "").toLowerCase();
        case "location": return (l.post_code || "").toLowerCase();
        case "created":  return new Date(l.created_at || 0).getTime();
        case "updated":  return new Date(l.last_updated || l.created_at || 0).getTime();
        default:         return 0;
      }
    };
    const dir = sortDir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [data.items, data.stages, sortKey, sortDir]);
  const activityIds = useMemo(() => sortedItems.map((l) => l.id), [sortedItems]);
  const { summaries: activitySummaries, refresh: refreshActivity } = useActivitySummaries(activityIds);

  // Clear selection on filter/view resets — but NOT when scrolling appends rows.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { clearSelection(); }, [search, stages, sources, ams, rot, hasQuote, sort, pageSize, view]);

  const sourceOptions = useMemo(() => Object.keys(data.facets?.sources || {}).sort(), [data.facets]);
  const amOptions     = useMemo(() => Object.keys(data.facets?.assignees || {}).sort(), [data.facets]);

  return (
    <LeadQuotePeekCtx.Provider value={setPeekQuoteId}>
    <NotesPeekProvider onOpen360={setPerson360Id}>
    <Layout mainProps={theme === "light" ? { "data-talmia-theme": "light" } : {}}>
      <div className="space-y-5" data-testid="lead-pipeline-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-5 flex-wrap">
           <div>
            <div className="text-[10px] uppercase tracking-widest text-sky-400 font-mono mb-1 flex items-center gap-1.5">
              <Sprout className="w-3 h-3" /> Lead Pipeline
            </div>
            <h1 className="text-3xl font-display font-bold tracking-tight flex items-center gap-3">
              Leads
              <LiveCount shown={data.items?.length || 0} total={data.total_matching || data.total || 0} noun="lead" testId="leads-live-count" />
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              <span className="text-white font-semibold">{data.total}</span> net-new{" "}
              customer{data.total === 1 ? "" : "s"} — no orders yet.
              <span className="text-slate-600 ml-2 text-[11px] font-mono">
                converts to a Deal on first order
              </span>
            </p>
           </div>
           {view === "table" && (stages.length > 0 || isDateFilterActive(dateRange)) && data.stats && (
             <FilteredStatsBox stats={data.stats} pipeline="leads" periodLabel={isDateFilterActive(dateRange) ? dateFilterLabel(dateRange) : null} />
           )}
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
              <button type="button" onClick={() => setView("table")} data-testid="lead-view-table"
                className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 ${view === "table" ? "bg-sky-500/15 text-sky-200" : "text-slate-400 hover:text-white"}`}>
                <TableIcon className="w-3 h-3" /> Table
              </button>
              <button type="button" onClick={() => setView("board")} data-testid="lead-view-board"
                className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 ${view === "board" ? "bg-sky-500/15 text-sky-200" : "text-slate-400 hover:text-white"}`}>
                <LayoutGrid className="w-3 h-3" /> Board
              </button>
            </div>
            <button type="button" onClick={load} disabled={loading} data-testid="leads-refresh"
              className="w-9 h-9 grid place-items-center rounded-lg border border-white/10 bg-white/[0.03] text-slate-300 hover:text-white hover:bg-white/[0.06] disabled:opacity-50"
              aria-label="Refresh">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={() => setShowNew(true)}
              data-testid="new-lead-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500/15 border border-sky-500/40 text-sky-200 text-sm hover:bg-sky-500/25"
            >
              <UserPlus className="w-3.5 h-3.5" /> New lead
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2.5" data-testid="leads-filters">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-[420px]">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, company, email, phone…"
                data-testid="leads-search"
                className="w-full pl-10 pr-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 focus:border-sky-400/60 outline-none text-sm text-white placeholder:text-slate-500"
              />
            </div>
            <div className="ml-auto flex items-center gap-2">
              <ArrowUpDown className="w-3.5 h-3.5 text-slate-500" />
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="leads-sort"
                className="dark-select border border-white/10 rounded-md text-[12px] px-2 py-1 outline-none focus:border-sky-400/60">
                <option value="created">Newest first</option>
                <option value="updated">Last updated</option>
                <option value="rot">Rot · most overdue</option>
              </select>
              <label className="text-[11px] font-mono uppercase tracking-widest text-slate-400 pl-2">Show</label>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                data-testid="leads-page-size"
                className="dark-select border border-white/10 rounded-md text-[12px] px-2 py-1 outline-none focus:border-sky-400/60"
                title="Rows per fetch — lower = less bandwidth"
              >
                {[25, 50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="text-[10px] text-slate-500 font-mono" data-testid="leads-count-summary">
                {data.total ? `${data.items?.length || 0} of ${data.total_matching || data.total}` : ""}
              </span>
            </div>
          </div>
          {/* Compact filter row — shared grammar with Deal pipeline: Core (AM ·
              Rot · Quote), then Source below. Tier removed — leads have no orders
              so tier is near-always blank. Build / urgency / ASAP / focus omitted
              (quote-driven, not meaningful for leads). */}
          <div className="flex items-center gap-2 flex-wrap text-[12px]">
            {myAmName && (
              <button
                type="button"
                onClick={() => setMine((v) => !v)}
                data-testid="leads-mine-toggle"
                title={mine ? "Showing only YOUR leads (your whole book). Click to see everyone's." : "Show only your leads"}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md border font-mono uppercase tracking-wider text-[11px] font-semibold transition-colors ${
                  mine
                    ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/60 mine-active"
                    : "bg-transparent text-slate-400 border-white/15 hover:text-white hover:border-white/30"
                }`}
              >
                <UserCheck className="w-3.5 h-3.5" /> My Leads
              </button>
            )}
            {managers.length > 0 && (
              <select value={ams[0] || ""} disabled={mine && !!myAmName} onChange={(e) => setAms(e.target.value ? [e.target.value] : [])} data-testid="leads-filter-am" className={`dark-select border border-white/10 rounded-md px-2 py-1.5 outline-none focus:border-sky-400/60 disabled:opacity-40 disabled:cursor-not-allowed${ams.length && !(mine && myAmName) ? " filter-active" : ""}`} title={mine && myAmName ? "Turn off 'My Leads' to filter by another AM" : ""}>
                <option value="">{mine && myAmName ? "My Leads (on)" : "All AMs"}</option>
                {managers.map((m) => <option key={m.email || m.id} value={m.email}>{m.name}</option>)}
              </select>
            )}
            <select value={rot[0] || ""} onChange={(e) => setRot(e.target.value ? [e.target.value] : [])} data-testid="leads-filter-rot" className={`dark-select border border-white/10 rounded-md px-2 py-1.5 outline-none focus:border-sky-400/60${rot.length ? " filter-active" : ""}`}>
              <option value="">Rot: any</option>
              <option value="rotting">Rotting</option>
              <option value="healthy">Healthy</option>
            </select>
            <select value={hasQuote[0] || ""} onChange={(e) => setHasQuote(e.target.value ? [e.target.value] : [])} data-testid="leads-filter-hasquote" className={`dark-select border border-white/10 rounded-md px-2 py-1.5 outline-none focus:border-sky-400/60${hasQuote.length ? " filter-active" : ""}`}>
              <option value="">Quote: any</option>
              <option value="true">Has open quote</option>
              <option value="false">No quote</option>
            </select>

            {view === "table" && (
              <>
                <span className="w-px h-5 bg-white/10 mx-1" />
                <PipelineStageFilter
                  stages={data.stages || []}
                  selected={stages}
                  onChange={setStages}
                  facets={data.facets?.stages || {}}
                  testidPrefix="leads-stage"
                />
              </>
            )}
            <DateRangeFilter value={dateRange} onChange={setDateRange} fields={LEAD_DATE_FIELDS} testid="leads-date-filter" />

            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearAll}
                data-testid="leads-clear-filters"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 text-[11px] font-mono uppercase tracking-wider font-semibold transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Clear filters ({activeFilterCount})
              </button>
            )}
          </div>
          {/* Source chip row kept below, as before. */}
          <div className="flex items-center gap-4 flex-wrap">
            {sourceOptions.length > 0 && (
              <ChipRow label="Source" options={sourceOptions.map((a) => ({ value: a, label: a }))} selected={sources} onChange={setSources} facets={data.facets?.sources} testid="lead-source" />
            )}
          </div>

          {/* Saved views — one-click filter presets, localStorage-backed. */}
          <div className="pt-1 border-t border-white/[0.04]">
            <SavedViewChips
              views={savedViews.views}
              currentSnapshot={viewSnapshot}
              onApply={applySavedView}
              onDelete={savedViews.remove}
              onSave={savedViews.saveCurrent}
              testidPrefix="lead-view"
            />
          </div>
        </div>

        {/* Bulk stage-assign bar — table multi-select (mirrors Deal pipeline) */}
        {view === "table" && selectedIds.size > 0 && (
          <div className="rounded-lg border border-teal-500/30 bg-teal-500/[0.06] px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="leads-bulk-bar">
            <span className="inline-flex items-center gap-2 text-[12px] text-teal-100 font-medium">
              <Layers className="w-4 h-4 text-teal-300" />
              {selectedIds.size} selected
            </span>
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Move to</label>
              <select
                defaultValue=""
                disabled={bulkBusy}
                onChange={(e) => { const v = e.target.value; e.target.value = ""; bulkAssignStage(v); }}
                data-testid="leads-bulk-stage"
                className="dark-select border border-white/10 rounded-md text-[12px] px-2 py-1.5 outline-none focus:border-teal-400/60 disabled:opacity-50"
              >
                <option value="" disabled>Select stage…</option>
                {data.stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              {bulkBusy && <Loader2 className="w-4 h-4 animate-spin text-teal-300" />}
              <button
                type="button"
                onClick={clearSelection}
                data-testid="leads-bulk-clear"
                className="text-[11px] font-mono text-slate-400 hover:text-white inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm px-3 py-2" data-testid="leads-error">
            {error}
          </div>
        )}

        <BufferProgressBar
          loaded={data.items?.length || 0}
          total={data.total_matching ?? data.total ?? 0}
          active={(hasMore || loadingMore) && (data.items?.length || 0) > 0}
        />
        {loading && !data.items.length ? (
          <div className="p-12 text-center text-slate-500" data-testid="leads-loading">Loading leads…</div>
        ) : view === "table" ? (
          <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="leads-table-wrap">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead className="bg-sky-500/10 border-b border-sky-500/20">
                  <tr className="text-[10px] font-mono uppercase tracking-widest text-sky-200">
                    <th className="py-2 pl-3 pr-1 w-6">
                      <button
                        type="button"
                        onClick={() => {
                          const pageIds = sortedItems.map((l) => l.id);
                          const allSel = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
                          setSelectedIds(allSel ? new Set() : new Set(pageIds));
                        }}
                        className="w-5 h-5 grid place-items-center rounded text-sky-200/70 hover:text-teal-300"
                        data-testid="leads-select-all"
                        aria-label="Select all on page"
                      >
                        {sortedItems.length > 0 && sortedItems.every((l) => selectedIds.has(l.id))
                          ? <CheckSquare className="w-4 h-4 text-teal-300" /> : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                    <th className="py-2 pl-1 pr-2 w-8"></th>
                    <SortTh label="Lead · Company" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <SortTh label="Stage" sortKey="stage" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <SortTh label="Source" sortKey="source" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <SortTh label="Phone" sortKey="phone" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <SortTh label="Quotes" sortKey="quotes" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} align="right" />
                    <SortTh label="AM" sortKey="am" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <SortTh label="Location (postcode)" sortKey="location" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <th className="py-2 pr-3 text-left">Activity</th>
                    <SortTh label="Updated" sortKey="updated" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center py-16 text-slate-500 text-sm">
                        No leads yet. Click <span className="text-sky-300 font-medium">+ New lead</span> to capture one, or create a quote for someone new — they&rsquo;ll land here automatically.
                      </td>
                    </tr>
                  )}
                  {sortedItems.map((l) => (
                    <LeadRow
                      activitySummary={activitySummaries[l.id]}
                      onActivitySaved={refreshActivity}
                      key={l.id}
                      lead={l}
                      stages={data.stages}
                      expanded={!!expandedIds[l.id]}
                      onToggleExpand={toggleExpand}
                      onNavigate={(id) => setPerson360Id(id)}
                      onNewQuote={newQuoteFor}
                      onQuoteArchived={() => load()}
                      onOwnerChanged={() => load()}
                      onStageChange={changeStage}
                      selected={selectedIds.has(l.id)}
                      onToggleSelect={toggleSelect}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {(data.items?.length || 0) > 0 && (
              <InfiniteSentinel
                onReach={loadMore}
                hasMore={hasMore}
                loading={loadingMore}
                count={data.items?.length || 0}
                total={data.total_matching || data.total || 0}
                testId="leads-sentinel"
              />
            )}
          </div>
        ) : (
          <div
            className="space-y-2"
            onMouseEnter={() => { setBoardHover(true); updateEdges(); }}
            onMouseLeave={() => setBoardHover(false)}
            onScrollCapture={updateEdges}
          >
            {/* Board scroll arrows — fade in on hover; disabled at the ends */}
            <div className="flex items-center justify-between gap-2" data-testid="lead-board-scroll-controls">
              <span className="text-[10.5px] text-slate-500 font-mono uppercase tracking-widest">
                {data.stages.length} stages · {data.items?.length || 0} leads
              </span>
              <div className={`flex items-center gap-1.5 transition-opacity duration-200 ${boardHover ? "opacity-100" : "opacity-0"}`}>
                <button
                  type="button"
                  onClick={() => scrollBoard(-1)}
                  disabled={atStart}
                  data-testid="lead-board-scroll-left"
                  aria-label="Scroll board left"
                  className="w-8 h-8 grid place-items-center rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-white/25 hover:bg-white/[0.04] transition-colors disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-slate-400"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollBoard(1)}
                  disabled={atEnd}
                  data-testid="lead-board-scroll-right"
                  aria-label="Scroll board right"
                  className="w-8 h-8 grid place-items-center rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-white/25 hover:bg-white/[0.04] transition-colors disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-slate-400"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            <BoardView leads={data.items} stages={data.stages} onNavigate={(id) => setPerson360Id(id)} onStageChange={changeStage} containerRef={boardRef} />
            {hasMore && (
              <div className="flex justify-center py-3">
                <button type="button" onClick={loadMore} disabled={loadingMore} data-testid="lead-board-load-more"
                  className="px-5 py-2 rounded-lg border border-sky-500/40 bg-sky-500/10 text-sky-200 text-[13px] font-medium hover:bg-sky-500/20 disabled:opacity-50">
                  {loadingMore ? "Loading…" : `Load more leads (${data.items?.length || 0} of ${data.total_matching || data.total || 0})`}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="text-[10.5px] text-slate-600 font-mono">
          A lead becomes a Deal automatically when their first order lands. Existing customers with LTV live under <Link to="/deals" className="text-orange-300 hover:underline">Deal Pipeline</Link>.
        </div>
      </div>

      <NewLeadModal open={showNew} onClose={() => setShowNew(false)} onCreated={() => load()} />
      <Person360Overlay personId={person360Id} onClose={() => setPerson360Id(null)} />
      <QuotePeekModal quoteId={peekQuoteId} open={!!peekQuoteId} onClose={() => setPeekQuoteId(null)} />
    </Layout>
    </NotesPeekProvider>
    </LeadQuotePeekCtx.Provider>
  );
}
